import React, { createContext, useState, useEffect } from "react";

export const ThemeContext = createContext();

export const lightTheme = {
  name: "light",
  bg: {
    primary: "#f8f6f3",
    secondary: "#ede8e1",
    tertiary: "#e4ddd4",
    dark: "#dcd4c9",
  },
  text: {
    primary: "#1a1a1a",
    secondary: "#4a4a4a",
    tertiary: "#777777",
    light: "#9a9a9a",
  },
  border: "#d9d0c3",
  accent: "#c9a84c",
  statusColors: {
    pending: "#ca5010",
    approved: "#107c10",
    executed: "#0078d4",
    inreview: "#8764b8",
    expired: "#a4262c",
    cancelled: "#6a6a6a",
  },
  hover: "rgba(201, 168, 76, 0.08)",
};

export const darkTheme = {
  name: "dark",
  bg: {
    primary: "#060e1f",
    secondary: "#0d1e35",
    tertiary: "#0a1628",
    dark: "#1e3054",
  },
  text: {
    primary: "#f0ede8",
    secondary: "#8fa3c0",
    tertiary: "#c8c4bc",
    light: "#1e3054",
  },
  border: "#1e3054",
  accent: "#c9a84c",
  statusColors: {
    pending: "#ca5010",
    approved: "#107c10",
    executed: "#0078d4",
    inreview: "#8764b8",
    expired: "#a4262c",
    cancelled: "#6a6a6a",
  },
  hover: "rgba(201, 168, 76, 0.04)",
};

export const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(true);

  useEffect(() => {
    const saved = localStorage.getItem("theme");
    if (saved) {
      setIsDark(saved === "dark");
    } else {
      setIsDark(true);
    }
  }, []);

  useEffect(() => {
    // Apply dark class to document root for Tailwind dark mode
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  const toggleTheme = () => {
    setIsDark(prev => {
      const newValue = !prev;
      localStorage.setItem("theme", newValue ? "dark" : "light");
      document.documentElement.classList.toggle('dark', newValue);
      return newValue;
    });
  };

  const theme = isDark ? darkTheme : lightTheme;

  return (
    <ThemeContext.Provider value={{ theme, isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

// Helper to replace hardcoded dark colors with theme colors
export const getDynamicColor = (theme, isDark, color) => {
  const colorMap = {
    "#0d1e35": theme.bg.secondary,
    "#0a1628": theme.bg.tertiary,
    "#111f38": theme.bg.secondary,
    "#1e3054": theme.border,
    "#8fa3c0": theme.text.secondary,
    "#f0ede8": theme.text.primary,
    "#c8c4bc": theme.text.tertiary,
    "#060e1f": theme.bg.primary,
    "#c9a84c": isDark ? "#c9a84c" : "#b8860b",
  };
  return colorMap[color] || color;
};
